/**
 * 수입관리 BdIncome API (isBdApiEnabled 시 /common/select|save)
 */
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
  diffRowsByUid,
} from 'services/commonQuery';

export function dimsKey(dims = {}) {
  return `${String(dims.tenantCode || '').trim()}|${String(dims.fiscalYear || '').trim()}`;
}

export function dimsParams(dims = {}) {
  return {
    tenantCode: String(dims.tenantCode || '').trim(),
    fiscalYear: Number(dims.fiscalYear) || dims.fiscalYear,
  };
}

function clearMod(rows) {
  return (rows || []).map((r) => ({ ...r, mod: '' }));
}

async function saveSimpleList({ dims, cache, loadFn, uidKey, saveQuery, nextRows, ensureUid }) {
  const key = dimsKey(dims);
  const params = dimsParams(dims);
  const prev = cacheGet(cache, key);
  const prevRows = Array.isArray(prev) ? prev : prev || (await loadFn(dims));
  const dirty = diffRowsByUid(prevRows, nextRows, uidKey, ensureUid);
  if (dirty.length) await commonSaveList(saveQuery, dirty, params);
  return loadFn(dims);
}

async function saveParentChildren({
  dims,
  cache,
  loadFn,
  parentUidKey,
  childUidKey,
  parentSaveQuery,
  childSaveQuery,
  nextParents,
  nextChildren,
  ensureParentUid,
  ensureChildUid,
  childListKey = 'vendors',
}) {
  const key = dimsKey(dims);
  const params = dimsParams(dims);
  const prevBundle = cacheGet(cache, key) || (await loadFn(dims));
  const prevParents = prevBundle.masters || [];
  const prevChildren = prevBundle[childListKey] || [];

  const dirtyP = diffRowsByUid(prevParents, nextParents, parentUidKey, ensureParentUid);
  const deletedParentUids = new Set(dirtyP.filter((r) => r.mod === 'D').map((r) => r[parentUidKey]));
  const childrenKept = (nextChildren || []).filter((c) => !deletedParentUids.has(c[parentUidKey]));
  const dirtyC = [
    ...diffRowsByUid(prevChildren, childrenKept, childUidKey, ensureChildUid),
    ...prevChildren
      .filter((c) => deletedParentUids.has(c[parentUidKey]))
      .map((c) => ({ ...c, mod: 'D' })),
  ];

  const run = async (query, rows) => {
    if (rows?.length) await commonSaveList(query, rows, params);
  };
  await run(childSaveQuery, dirtyC.filter((r) => r.mod === 'D'));
  await run(parentSaveQuery, dirtyP.filter((r) => r.mod === 'D'));
  await run(parentSaveQuery, dirtyP.filter((r) => r.mod === 'A' || r.mod === 'U'));
  await run(childSaveQuery, dirtyC.filter((r) => r.mod === 'A' || r.mod === 'U'));
  return loadFn(dims);
}

export const SAVE_Q = {
  source: ['BdIncome.insertSource', 'BdIncome.updateSource', 'BdIncome.deleteSource'],
  resolution: ['BdIncome.insertResolution', 'BdIncome.updateResolution', 'BdIncome.deleteResolution'],
  resolutionVendor: [
    'BdIncome.insertResolutionVendor',
    'BdIncome.updateResolutionVendor',
    'BdIncome.deleteResolutionVendor',
  ],
  receipt: ['BdIncome.insertReceipt', 'BdIncome.updateReceipt', 'BdIncome.deleteReceipt'],
  adjust: ['BdIncome.insertAdjust', 'BdIncome.updateAdjust', 'BdIncome.deleteAdjust'],
  overpay: ['BdIncome.insertOverpay', 'BdIncome.updateOverpay', 'BdIncome.deleteOverpay'],
  writeoff: ['BdIncome.insertWriteoff', 'BdIncome.updateWriteoff', 'BdIncome.deleteWriteoff'],
};

const sourceCache = new Map();
const resolutionCache = new Map();
const receiptCache = new Map();
const adjustCache = new Map();
const overpayCache = new Map();
const writeoffCache = new Map();

export { isBdApiEnabled, cacheGet, cacheSet, clearMod };

export async function apiLoadSources(dims) {
  const rows = clearMod(await commonSelect('BdIncome.selectSourceList', dimsParams(dims)));
  cacheSet(sourceCache, dimsKey(dims), rows);
  return rows;
}

export async function apiSaveSources(dims, rows, ensureUid) {
  return saveSimpleList({
    dims,
    cache: sourceCache,
    loadFn: apiLoadSources,
    uidKey: 'sourceUid',
    saveQuery: SAVE_Q.source,
    nextRows: (rows || []).filter((r) => r && r.mod !== 'D'),
    ensureUid,
  });
}

export async function apiLoadResolutionBundle(dims) {
  const params = dimsParams(dims);
  const [masters, vendors] = await Promise.all([
    commonSelect('BdIncome.selectResolutionList', params),
    commonSelect('BdIncome.selectResolutionVendorList', params),
  ]);
  const bundle = { masters: clearMod(masters), vendors: clearMod(vendors) };
  cacheSet(resolutionCache, dimsKey(dims), bundle);
  return bundle;
}

export async function apiSaveResolutionBundle(dims, masters, vendors, ensureMasterUid, ensureDetailUid) {
  return saveParentChildren({
    dims,
    cache: resolutionCache,
    loadFn: apiLoadResolutionBundle,
    parentUidKey: 'resolutionUid',
    childUidKey: 'detailUid',
    parentSaveQuery: SAVE_Q.resolution,
    childSaveQuery: SAVE_Q.resolutionVendor,
    nextParents: (masters || []).filter((m) => m && m.mod !== 'D'),
    nextChildren: (vendors || []).filter((v) => v && v.mod !== 'D'),
    ensureParentUid: ensureMasterUid,
    ensureChildUid: ensureDetailUid,
  });
}

export async function apiLoadReceipts(dims) {
  const masters = clearMod(await commonSelect('BdIncome.selectReceiptList', dimsParams(dims)));
  cacheSet(receiptCache, dimsKey(dims), masters);
  return { masters };
}

export async function apiSaveReceipts(dims, masters, ensureUid) {
  const saved = await saveSimpleList({
    dims,
    cache: receiptCache,
    loadFn: async (d) => {
      const b = await apiLoadReceipts(d);
      return b.masters;
    },
    uidKey: 'receiptUid',
    saveQuery: SAVE_Q.receipt,
    nextRows: (masters || []).filter((r) => r && r.mod !== 'D'),
    ensureUid,
  });
  return { masters: Array.isArray(saved) ? saved : saved?.masters || [] };
}

export async function apiLoadAdjusts(dims) {
  const rows = clearMod(await commonSelect('BdIncome.selectAdjustList', dimsParams(dims)));
  cacheSet(adjustCache, dimsKey(dims), rows);
  return rows;
}

export async function apiSaveAdjusts(dims, rows, ensureUid) {
  return saveSimpleList({
    dims,
    cache: adjustCache,
    loadFn: apiLoadAdjusts,
    uidKey: 'adjustUid',
    saveQuery: SAVE_Q.adjust,
    nextRows: (rows || []).filter((r) => r && r.mod !== 'D'),
    ensureUid,
  });
}

export async function apiLoadOverpays(dims) {
  const rows = clearMod(await commonSelect('BdIncome.selectOverpayList', dimsParams(dims)));
  cacheSet(overpayCache, dimsKey(dims), rows);
  return rows;
}

export async function apiSaveOverpays(dims, rows, ensureUid) {
  return saveSimpleList({
    dims,
    cache: overpayCache,
    loadFn: apiLoadOverpays,
    uidKey: 'overpayUid',
    saveQuery: SAVE_Q.overpay,
    nextRows: (rows || []).filter((r) => r && r.mod !== 'D'),
    ensureUid,
  });
}

export async function apiLoadWriteoffs(dims) {
  const rows = clearMod(await commonSelect('BdIncome.selectWriteoffList', dimsParams(dims)));
  cacheSet(writeoffCache, dimsKey(dims), rows);
  return rows;
}

export async function apiSaveWriteoffs(dims, rows, ensureUid) {
  return saveSimpleList({
    dims,
    cache: writeoffCache,
    loadFn: apiLoadWriteoffs,
    uidKey: 'writeoffUid',
    saveQuery: SAVE_Q.writeoff,
    nextRows: (rows || []).filter((r) => r && r.mod !== 'D'),
    ensureUid,
  });
}
